import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'react-toastify'
import { Icon } from '@iconify/react'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import Input from '../components/common/Input'
import Button from '../components/common/Button'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { authService } from '../services/api/authService'
import { planService } from '../services/api/planService'
import { parsePhoneNumberToParts } from '../utils/phoneHelpers'

const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  contact: z.string().min(1, 'Contact number is required'),
  rememberMe: z.boolean().optional(),
})

const safeParseJson = (value) => {
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const Register = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const planId = searchParams.get('planId') || ''

  const [isLoading, setIsLoading] = useState(false)
  const [isPlanLoading, setIsPlanLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedPlan, setSelectedPlan] = useState(() => safeParseJson(localStorage.getItem('selectedPlan')))

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      contact: '',
    },
  })

  const contactValue = watch('contact')

  useEffect(() => {
    if (!planId) {
      toast.info('Please choose a plan first.')
      navigate('/plans', { replace: true })
    }
  }, [planId, navigate])

  useEffect(() => {
    const ensurePlan = async () => {
      if (!planId) return

      if (selectedPlan?._id === planId) return

      const stored = safeParseJson(localStorage.getItem('selectedPlan'))
      if (stored?._id === planId) {
        setSelectedPlan(stored)
        return
      }

      setIsPlanLoading(true)
      try {
        const data = await planService.list({ page: 1, limit: 50 })
        const plan = (data?.results || []).find((p) => p._id === planId)
        if (plan) setSelectedPlan(plan)
      } catch {
        // Plan details are optional for registration; ignore failures.
      } finally {
        setIsPlanLoading(false)
      }
    }

    ensurePlan()
  }, [planId, selectedPlan?._id])

  const planLabel = useMemo(() => {
    if (!selectedPlan) return planId ? `Plan ID: ${planId}` : ''
    const cycle = selectedPlan.billingCycle ? `/${selectedPlan.billingCycle}` : ''
    return `${selectedPlan.name} • ${selectedPlan.currency || 'USD'} ${selectedPlan.price}${cycle}`
  }, [selectedPlan, planId])

  const onSubmit = async (data) => {
    if (!planId) return

    setIsLoading(true)
    setError('')

    try {
      const { countryCode, contact } = parsePhoneNumberToParts(data.contact)

      const response = await authService.register({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        countryCode,
        contact,
        planId,
      })

      const checkoutUrl = response?.checkoutUrl
      if (checkoutUrl) {
        try {
          sessionStorage.setItem('checkoutUrl', checkoutUrl)
        } catch {
          // ignore storage errors
        }
      }

      toast.success(response?.message || 'Registered successfully. Continue to checkout.')
      navigate('/checkout', { state: { checkoutUrl, message: response?.message, user: response?.user } })
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Registration failed. Please try again.'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-2xl mb-4 shadow-lg">
            <Icon icon="mdi:shield-check" className="text-3xl text-white" />
          </div>
          <h1 className="text-3xl font-bold text-text-dark mb-2">Create your account</h1>
          <p className="text-text-secondary">Complete registration to proceed to payment.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border-light">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-text-dark">Registration</h2>
              <p className="text-sm text-text-secondary">
                {isPlanLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    Loading plan...
                  </span>
                ) : (
                  planLabel
                )}
              </p>
            </div>
            <Link to="/plans" className="text-sm text-primary hover:text-primary-dark font-medium">
              Change plan
            </Link>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
            <input type="hidden" {...register('contact')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                placeholder="jone"
                {...register('firstName')}
                error={errors.firstName?.message}
              />
              <Input
                label="Last Name"
                placeholder="doe"
                {...register('lastName')}
                error={errors.lastName?.message}
              />
            </div>

            <Input
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              {...register('email')}
              error={errors.email?.message}
            />

            <div className="space-y-2">
              <label className="form-label">Contact Number</label>
              <div className={`relative border rounded-lg p-2 ${errors.contact ? 'border-danger' : 'border-border-light'}`}>
                <PhoneInput
                  international
                  defaultCountry="PK"
                  value={contactValue || ''}
                  onChange={(value) => {
                    setValue('contact', value || '', { shouldValidate: true, shouldDirty: true })
                  }}
                  onBlur={() => setValue('contact', getValues('contact'), { shouldValidate: true })}
                  placeholder="Enter phone number"
                  className="w-full"
                />
              </div>
              {errors.contact && <p className="mt-1 text-sm text-danger">{errors.contact.message}</p>}
            </div>

            <div className="flex items-center justify-between py-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('rememberMe')}
                  className="w-4 h-4 text-primary border-border-light rounded focus:ring-primary"
                />
                <span className="text-sm text-text-secondary">Remember me</span>
              </label>
              <Link to="/login" className="text-sm text-primary hover:text-primary-dark font-medium">
                Back to Login
              </Link>
            </div>

            <Button type="submit" disabled={isLoading} className="w-full mt-4" icon="mdi:account-plus">
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center text-sm text-text-secondary">
          <p>Â© 2024 SentryPrime. All rights reserved.</p>
        </div>
      </div>
    </div>
  )
}

export default Register
